/**
 * Auth configuration. This module is intentionally tiny: it lands in the
 * entry chunk (the Header needs `authEnabled` on every page), so anything
 * that touches the Cognito SDK lives in ./authClient.ts behind a dynamic
 * import and only ever downloads when auth is enabled AND needed.
 *
 * Dark-ship guarantee: when any of the three VITE_COGNITO_* vars is absent
 * at build time, `authEnabled` is false, every auth surface renders
 * nothing, and the auth chunk is never requested. The site behaves exactly
 * as it did before this feature existed.
 */
import type { AuthClient } from './authClient';

export interface CognitoConfig {
  userPoolId: string;
  clientId: string;
  /**
   * Cognito domain hostname, e.g.
   * aievals-auth.auth.ap-south-1.amazoncognito.com. The email OTP flow
   * never touches it; it exists for the Google sign-in redirect, whose
   * /oauth2/authorize and /oauth2/token endpoints live on this host.
   */
  domain: string;
}

function normalizeDomain(value: string): string {
  return value.replace(/^https?:\/\//, '').replace(/\/+$/, '');
}

function readConfig(): CognitoConfig | undefined {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID?.trim();
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID?.trim();
  const rawDomain = import.meta.env.VITE_COGNITO_DOMAIN?.trim();
  if (!userPoolId || !clientId || !rawDomain) return undefined;

  return {
    userPoolId,
    clientId,
    domain: normalizeDomain(rawDomain),
  };
}

export const cognitoConfig = readConfig();
export const authEnabled = cognitoConfig !== undefined;

/**
 * The "Continue with Google" button is built dark: the Google IdP slot in
 * terraform is inert until real OAuth credentials exist, so the button
 * only renders when the owner flips VITE_AUTH_GOOGLE=1 at build time.
 * Until then the pages are email-OTP only and /auth/callback redirects
 * away instead of running a code exchange.
 */
export const googleAuthEnabled = authEnabled && import.meta.env.VITE_AUTH_GOOGLE === '1';

/** Where to send the reader after the sign-in round trip. */
const RETURN_TO_KEY = 'auth:returnTo';

export function stashReturnTo(path: string): void {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  } catch {
    // storage unavailable (private mode quirks); default redirect covers it
  }
}

export function takeReturnTo(): string {
  try {
    const value = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    return value || '/';
  } catch {
    return '/';
  }
}

/** localStorage key for the token store owned by authClient.ts. */
export function tokenStorageKey(clientId: string): string {
  return `aievals.auth.${clientId}`;
}

/**
 * Cheap hint that a previous session exists, without downloading the auth
 * chunk. The token store in authClient.ts writes a single namespaced
 * localStorage record; its absence means anonymous, no network needed.
 */
export function hasStoredSession(): boolean {
  if (!cognitoConfig) return false;
  try {
    return localStorage.getItem(tokenStorageKey(cognitoConfig.clientId)) !== null;
  } catch {
    return false;
  }
}

let clientPromise: Promise<AuthClient> | null = null;

/**
 * Lazy singleton around the Cognito wrapper. The dynamic import is the
 * only edge into authClient.ts, so Rollup splits the AWS SDK into an
 * async chunk that anonymous readers never download unless they interact
 * with auth.
 */
export function loadAuthClient(): Promise<AuthClient> {
  const config = cognitoConfig;
  if (!config) {
    return Promise.reject(new Error('auth is not enabled in this build'));
  }
  // Reset on rejection: a cached rejected promise would make one flaky chunk
  // load (mobile network drop, deploy-swapped hash) permanently poison every
  // later submit, restore, and sign-out until a full page reload. With the
  // reset, "try again" actually retries the import.
  clientPromise ??= import('./authClient')
    .then((mod) => mod.createAuthClient(config))
    .catch((err: unknown) => {
      clientPromise = null;
      throw err;
    });
  return clientPromise;
}

/**
 * Current ID token (JWT). This is the token that carries the reader's
 * identity claims (email, sub) and the one server-side features verify;
 * the PDF download API wants exactly this. Silently refreshed when it is
 * within five minutes of expiry. Resolves null when signed out, when
 * auth is dark, or when the stored session cannot be refreshed; callers
 * treat null as anonymous.
 */
export async function getIdToken(): Promise<string | null> {
  if (!authEnabled || !hasStoredSession()) return null;
  try {
    const client = await loadAuthClient();
    return await client.getIdToken();
  } catch {
    return null;
  }
}

/**
 * Current access token (JWT) for Cognito-authorized APIs (scopes, not
 * identity claims). Same refresh and null semantics as getIdToken().
 */
export async function getAccessToken(): Promise<string | null> {
  if (!authEnabled || !hasStoredSession()) return null;
  try {
    const client = await loadAuthClient();
    return await client.getAccessToken();
  } catch {
    return null;
  }
}
